import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Mail, Download, Inbox, RefreshCw, Calendar, Tag } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

const sourceBadgeColor = (src) => {
  if (!src) return "bg-white text-stone-700 border-stone-300";
  if (src.includes("free-guide")) return "bg-white text-[#8B6508] border-[#F39A1F]";
  if (src.includes("smoke") || src.includes("test")) return "bg-white text-stone-700 border-stone-300";
  return "bg-[#F4EFE6] text-red-wine border-[#F39A1F]/40";
};

export default function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("");

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const r = await api.get("/leads/free-guide");
      setLeads(r.data.leads || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load leads");
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    try {
      const r = await api.get("/leads/free-guide/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "faitharama-leads.csv";
      document.body.appendChild(a); a.click();
      setTimeout(() => { window.URL.revokeObjectURL(url); a.remove(); }, 200);
      toast.success("Leads exported!");
    } catch (e) { toast.error("Export failed"); }
  };

  const filtered = leads.filter(l => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (l.name || "").toLowerCase().includes(q) || (l.email || "").toLowerCase().includes(q) || (l.source || "").toLowerCase().includes(q);
  });

  const today = new Date().toDateString();
  const todayCount = leads.filter(l => { try { return new Date(l.created_at).toDateString() === today; } catch { return false; } }).length;

  return (
    <div className="space-y-8" data-testid="leads-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-wider-cap text-gold-deep">Lead Capture</div>
          <h2 className="text-3xl uppercase text-red-wine">Free Guide Leads</h2>
          <p className="text-sm text-foreground mt-1">Every visitor who entered their email to grab the Free Guide.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => load({ silent: true })} disabled={refreshing} className="btn-outline-gold inline-flex items-center gap-2 !py-2 !px-4 !text-[11px]" data-testid="leads-refresh">
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}/>Refresh
          </button>
          <button onClick={exportCsv} disabled={leads.length === 0} className="btn-gold inline-flex items-center gap-2 !py-2 !px-4 !text-[11px]" data-testid="leads-export">
            <Download className="w-4 h-4"/>Export CSV
          </button>
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="faith-card p-6" data-testid="stat-leads-total">
          <Inbox className="w-7 h-7 text-gold-deep mb-3" strokeWidth={1.5}/>
          <div className="text-4xl font-serif-display text-red-wine">{leads.length}</div>
          <div className="text-sm text-foreground">Total leads captured</div>
        </div>
        <div className="faith-card p-6" data-testid="stat-leads-today">
          <Calendar className="w-7 h-7 text-gold-deep mb-3" strokeWidth={1.5}/>
          <div className="text-4xl font-serif-display text-red-wine">{todayCount}</div>
          <div className="text-sm text-foreground">Captured today</div>
        </div>
        <div className="faith-card p-6" data-testid="stat-leads-unique">
          <Mail className="w-7 h-7 text-gold-deep mb-3" strokeWidth={1.5}/>
          <div className="text-4xl font-serif-display text-red-wine">{new Set(leads.map(l=>l.email)).size}</div>
          <div className="text-sm text-foreground">Unique email addresses</div>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <input
          placeholder="Search name, email, or source…"
          value={filter}
          onChange={(e)=>setFilter(e.target.value)}
          className="flex-1 px-4 py-3 rounded-md border border-border bg-white text-sm"
          data-testid="leads-search"
        />
      </div>

      {/* Table */}
      <div className="faith-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-foreground">Loading leads…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="leads-empty">
            <Inbox className="w-12 h-12 text-gold-deep/60 mx-auto mb-3" strokeWidth={1.25}/>
            <div className="text-xl uppercase text-red-wine">No leads {filter ? "match your search" : "yet"}</div>
            <p className="text-sm text-foreground mt-2">{filter ? "Try a different search term." : "Share your Free Guide on the Books page or social media — leads will show up here automatically."}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4EFE6] border-b border-[#F39A1F]/30">
                <tr>
                  <th className="text-left px-5 py-3 text-[10px] tracking-wider-cap text-gold-deep">Name</th>
                  <th className="text-left px-5 py-3 text-[10px] tracking-wider-cap text-gold-deep">Email</th>
                  <th className="text-left px-5 py-3 text-[10px] tracking-wider-cap text-gold-deep">Source</th>
                  <th className="text-left px-5 py-3 text-[10px] tracking-wider-cap text-gold-deep">Captured</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l, i) => (
                  <tr key={l.id || i} className="border-b border-border/60 hover:bg-[#FFFFFF] transition-colors" data-testid={`lead-row-${i}`}>
                    <td className="px-5 py-3 font-semibold text-foreground">{l.name || "—"}</td>
                    <td className="px-5 py-3"><a href={`mailto:${l.email}`} className="text-red-wine hover:underline">{l.email}</a></td>
                    <td className="px-5 py-3"><span className={`inline-flex items-center gap-1.5 text-[10px] tracking-wider-cap font-bold uppercase border rounded-full px-2.5 py-1 ${sourceBadgeColor(l.source)}`}><Tag className="w-3 h-3"/>{l.source || "unknown"}</span></td>
                    <td className="px-5 py-3 text-foreground">{fmtDate(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
